import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import {
  Button,
  Card,
  CardHeader,
  Dialog,
  Bar,
  ComboBox,
  ComboBoxItem,
  Label,
  CheckBox,
  Input,
  Form,
  FormGroup,
  FormItem,
  AnalyticalTable,
} from "@ui5/webcomponents-react";
import httpRequest from "../../httpRequest";

const MailboxCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [mailboxes, setMailboxes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMailbox, setCurrentMailbox] = useState({
    id: "",
    server: "",
    port: "",
    username: "",
    password: "",
    company_id: "",
    function_id: "",
    allow_duplicates: false,
    employee_code: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [mailboxEmployees, setMailboxEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [isEditingEmployee, setIsEditingEmployee] = useState(false);

  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
};


  const emailProviders = [
    { name: "Office 365", server: "outlook.office365.com", port: 993 },
    { name: "Yahoo", server: "imap.mail.yahoo.com", port: 993 },
    { name: "Zoho", server: "imappro.zoho.com", port: 993 },
    { name: "Rackspace", server: "secure.emailsrvr.com", port: 993 },
    { name: "GoDaddy", server: "imap.secureserver.net", port: 993 },
    { name: "Namecheap", server: "mail.privateemail.com", port: 993 },
    { name: "Gmail", server: "imap.gmail.com", port: 993 },
    { name: "AWS US-West-2", server: "imap.mail.us-west-2.awsapps.com", port: 993 },
    { name: "AWS US-East-1", server: "imap.mail.us-east-1.awsapps.com", port: 993 },
    { name: "AWS EU-West-1", server: "imap.mail.eu-west-1.awsapps.com", port: 993 },
    { name: "Custom", server: "", port: "" },
  ];

  useEffect(() => {
    fetchMailboxes();
    fetchCompanies();
    fetchFunctions();
  }, []);

  useEffect(() => {
    if (currentMailbox.company_id && currentMailbox.function_id) {
      fetchMailboxEmployees();
    }
  }, [currentMailbox.company_id, currentMailbox.function_id]);

  const fetchMailboxes = async () => {
    setLoading(true);
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/mailboxes`);
      setMailboxes(response.data.mailboxes);
    } catch (error) {
      enqueueSnackbar("Failed to fetch mailboxes", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies`);
      setCompanies(response.data.companies);
    } catch (error) {
      enqueueSnackbar("Failed to fetch companies", { variant: "error" });
    }
  };

  const fetchFunctions = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/functions`);
      setFunctions(response.data.functions);
    } catch (error) {
      enqueueSnackbar("Failed to fetch functions", { variant: "error" });
    }
  };

  const fetchMailboxEmployees = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-mailbox-employees`, {
        params: {
          company_id: currentMailbox.company_id,
          function_id: currentMailbox.function_id,
        },
      });
      setMailboxEmployees(response.data.employees);
    } catch (error) {
      enqueueSnackbar("Failed to fetch mailbox employees", { variant: "error" });
    }
  };

  const handleAddClick = () => {
    setCurrentMailbox({
      id: "",
      server: "",
      port: "",
      username: "",
      password: "",
      company_id: "",
      function_id: "",
      allow_duplicates: false,
      employee_code: "",
    });
    setSelectedProvider("");
    setEditMode(false);
    setOpen(true);
  };

  const handleEditClick = (mailbox) => {
    const company = companies.find((c) => c.company_name === mailbox.company);
    const func = functions.find((f) => f.function_name === mailbox.function);
    
    setCurrentMailbox({
      ...mailbox,
      company_id: company ? company.id : "",
      function_id: func ? func.id : "",
      employee_code: mailbox.employee_code,
    });
  
    // Set selectedEmployee or a default message
    setSelectedEmployee(mailbox.employee_code ? mailbox.employee_code : "No employee selected");
  
    setSelectedProvider(
      emailProviders.find((p) => p.server === mailbox.server && p.port === mailbox.port)?.name || "Custom"
    );
    setEditMode(true);
    setOpen(true);
  };
  
  

  const handleDeleteClick = async (id) => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-mailbox/${id}`);
      enqueueSnackbar("Mailbox deleted successfully", { variant: "success" });
      fetchMailboxes();
    } catch (error) {
      enqueueSnackbar("Failed to delete mailbox", { variant: "error" });
    }
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentMailbox({
      id: "",
      server: "",
      port: "",
      username: "",
      password: "",
      company_id: "",
      function_id: "",
      allow_duplicates: false,
      employee_code: "",
    });
    setSelectedEmployee("");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setCurrentMailbox({
        ...currentMailbox,
        [name]: checked,
      });
    } else {
      setCurrentMailbox({
        ...currentMailbox,
        [name]: value,
      });
    }
  };

  const handleEmployeeChange = (e) => {
    const selectedEmployeeCode = e.target.value.split(":")[0];
    setSelectedEmployee(e.target.value);
    setCurrentMailbox({
      ...currentMailbox,
      employee_code: selectedEmployeeCode,
    });
  };

  const handleSubmit = async () => {
    try {
      if (!editMode) {
        const existingMailbox = mailboxes.find((mailbox) => mailbox.username === currentMailbox.username);
        if (existingMailbox) {
          enqueueSnackbar("Email already exists in the database", { variant: "error" });
          return;
        }
      }

      if (editMode) {
        await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/update-mailbox/${currentMailbox.id}`, currentMailbox);
        enqueueSnackbar("Mailbox updated successfully", { variant: "success" });
      } else {
        await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-mailbox`, currentMailbox);
        enqueueSnackbar("Mailbox added successfully", { variant: "success" });
      }
      fetchMailboxes();
      handleClose();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        enqueueSnackbar(error.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("Failed to save mailbox", { variant: "error" });
      }
    }
  };



  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

 const columns = [
    { accessor: 'server', Header: 'Server', autoResizable: true, },
    { accessor: 'port', Header: 'Port', autoResizable: true },
    { accessor: 'username', Header: 'Username', autoResizable: true },
    { accessor: 'company', Header: 'Company', autoResizable: true },
    { accessor: 'function', Header: 'Function', autoResizable: true },
    {
      Header: 'Actions',
      Cell: ({ row }) => (
        <>
          <ButtonOutlined onClick={() => handleEditClick(row.original)}>
            <EditIcon />
          </ButtonOutlined>
          <ButtonOutlined onClick={() => handleDeleteClick(row.original.id)}>
            <DeleteIcon />
          </ButtonOutlined>
        </>
      ),
    },
  ];

  const rows = mailboxes
  .filter((mailbox) =>
    mailbox.server.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mailbox.port.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    mailbox.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    companies.find(company => company.id === mailbox.company_id)?.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    functions.find(func => func.id === mailbox.function_id)?.function_name.toLowerCase().includes(searchTerm.toLowerCase())
  )
  .map((mailbox) => ({
    id: mailbox.id,
    server: mailbox.server,
    port: mailbox.port,
    username: mailbox.username,
    company: companies.find(company => company.id === mailbox.company_id)?.company_name,
    function: functions.find(func => func.id === mailbox.function_id)?.function_name,
    allow_duplicates: mailbox.allow_duplicates,
    employee_code: mailbox.employee_code, // Add this line
  }));


  return (
    <Card header={<CardHeader subtitleText="Create and manage mailboxes" titleText="Mailbox Management" />} style={{ width: "100%", overflow: "hidden" }}>
      <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <input
          type="text"
          placeholder="Search Mailbox..."
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ flex: "1", padding: "10px", marginRight: "20px" }}
        />
        <ButtonContained onClick={handleAddClick}>
          <AddIcon /> Add Mailbox
        </ButtonContained>
      </div>
      <AnalyticalTable
          data={rows}
          columns={columns}
          sortable
            visibleRows={8}
            scaleWidthMode="Smart"
            loading={loading}
            style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px' }}
        />

      <Dialog headerText={editMode ? "Edit Mailbox" : "Add Mailbox"} open={open} style={{ width: "90%" }} onAfterClose={handleClose} footer={
        <Bar
          design="Footer"
          endContent={
            <>
              <ButtonOutlined onClick={handleClose}>Cancel</ButtonOutlined>
              <ButtonContained style={{ marginLeft: 8 }} onClick={handleSubmit}>{editMode ? "Update" : "Add"}</ButtonContained>
            </>
          }
        />
      }>
<Form layout="S1 M1 L2 XL2">
      <FormGroup headerText="Provider Information">
        <FormItem labelContent={<Label>Provider</Label>}>
          <ComboBox
            value={selectedProvider}
            onChange={(e) => {
              const provider = emailProviders.find(p => p.name === e.target.value);
              if (provider) {
                setSelectedProvider(e.target.value);
                setCurrentMailbox({
                  ...currentMailbox,
                  server: provider.server,
                  port: provider.port,
                });
              }
            }}
          >
            {emailProviders.map((provider) => (
              <ComboBoxItem key={provider.name} text={provider.name} />
            ))}
          </ComboBox>
        </FormItem>

        <FormItem labelContent={<Label>Server</Label>}>
          <Input
            name="server"
            placeholder="Server"
            value={currentMailbox.server}
            onInput={handleChange}
          />
        </FormItem>

        <FormItem labelContent={<Label>Port</Label>}>
          <Input
            name="port"
            placeholder="Port"
            type="Number"
            value={currentMailbox.port}
            onInput={handleChange}
          />
        </FormItem>

        <FormItem labelContent={<Label>Username</Label>}>
          <Input
            name="username"
            placeholder="Username"
            value={currentMailbox.username}
            onInput={handleChange}
          />
        </FormItem>

        <FormItem labelContent={<Label>Password</Label>}>
          <Input
            name="password"
            placeholder="Password"
            type={showPassword ? "Text" : "Password"}
            value={currentMailbox.password}
            onInput={handleChange}
            endAdornment={
              <Button onClick={togglePasswordVisibility}>
                {showPassword ? "Hide" : "Show"}
              </Button>
            }
          />
        </FormItem>
      </FormGroup>

      <FormGroup headerText="Company Information">
        <FormItem labelContent={<Label>Company</Label>}>
          <ComboBox
            value={companies.find(company => company.id === currentMailbox.company_id)?.company_name || ''}
            onChange={(e) => {
              const company = companies.find(company => company.company_name === e.target.value);
              setCurrentMailbox({
                ...currentMailbox,
                company_id: company ? company.id : '',
              });
            }}
          >
            {companies.map((company) => (
              <ComboBoxItem key={company.id} text={company.company_name} />
            ))}
          </ComboBox>
        </FormItem>

        <FormItem labelContent={<Label>Function</Label>}>
          <ComboBox
            value={functions.find(func => func.id === currentMailbox.function_id)?.function_name || ''}
            onChange={(e) => {
              const func = functions.find(func => func.function_name === e.target.value);
              setCurrentMailbox({
                ...currentMailbox,
                function_id: func ? func.id : '',
              });
            }}
          >
            {functions.map((func) => (
              <ComboBoxItem key={func.id} text={func.function_name} />
            ))}
          </ComboBox>
        </FormItem>

        <FormItem>
          <CheckBox
            text="Allow Duplicate Scans"
            name="allow_duplicates"
            checked={currentMailbox.allow_duplicates}
            onChange={handleChange}
          />
        </FormItem>
      </FormGroup>
      <FormGroup headerText="Mailbox User">
  <FormItem labelContent={<Label>Mailbox Initiator</Label>}>
    
    <ComboBox
      value={selectedEmployee}
      onChange={handleEmployeeChange}
      disabled={!currentMailbox.company_id || !currentMailbox.function_id}
    >
      {mailboxEmployees.map((employee) => (
        <ComboBoxItem key={employee.employee_code} text={`${employee.employee_code}: ${employee.employee_name}`} />
      ))}
    </ComboBox>
  </FormItem>
</FormGroup>

    </Form>
      </Dialog>
    </Card>
  );
};

export default MailboxCard;
